import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { CS_SPECIALIZATIONS } from "../../../data/home-data";
import { DOMAIN_IMAGES } from "../../../data/programmes";

// SMRU-only image overrides (don't touch the shared DOMAIN_IMAGES used elsewhere).
const IMAGE_OVERRIDES = {
  "Artificial Intelligence & Machine Learning": "/general/CONTEXT.png",
};

// Anchor id for each specialization card, so footer links can jump to the exact card.
export const specSlug = (name) =>
  "spec-" + name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.09 },
  }),
};

export default function SmruSpecializations() {
  // Highlight the card whose anchor was navigated to (e.g. from the footer).
  const [activeId, setActiveId] = useState(null);
  useEffect(() => {
    let timer;
    const onHash = () => {
      const id = window.location.hash.slice(1);
      if (id.startsWith("spec-")) {
        setActiveId(id);
        clearTimeout(timer);
        timer = setTimeout(() => setActiveId(null), 2800);
      }
    };
    onHash();
    window.addEventListener("hashchange", onHash);
    return () => {
      window.removeEventListener("hashchange", onHash);
      clearTimeout(timer);
    };
  }, []);

  return (
    <section
      id="program"
      className="py-20 sm:py-28 bg-gradient-to-b from-aipeach/30 via-white to-aisky/20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-aiblue/10 text-aiblue">
            INSIDE OUR ECOSYSTEM
          </span>
          <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-gray-900">
            Five Future-Ready Specializations
          </h2>
          <p className="mt-4 text-gray-600 text-base sm:text-lg">
            Pick the path that fits your ambition every track is built with guaranteed career
            outcomes.
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {CS_SPECIALIZATIONS.map((spec, i) => (
            <motion.div
              key={spec.name}
              id={specSlug(spec.name)}
              custom={i}
              variants={cardVariants}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              className={`group flex flex-col overflow-hidden rounded-[2rem] bg-white border-2 scroll-mt-24 transition duration-300 hover:-translate-y-1 hover:shadow-xl ${
                activeId === specSlug(spec.name)
                  ? "border-aiblue shadow-xl -translate-y-1"
                  : spec.featured
                  ? "border-aiorange shadow-lg shadow-aiorange/10"
                  : "border-transparent shadow-[0_10px_30px_rgba(0,0,0,0.06)] hover:border-aiblue"
              }`}
            >
              {/* Image header */}
              <div className="relative h-44 overflow-hidden">
                <img
                  src={IMAGE_OVERRIDES[spec.name] || DOMAIN_IMAGES[spec.name]}
                  alt={spec.name}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
                <h3 className="absolute bottom-4 left-5 right-5 font-display font-bold text-lg text-white leading-tight drop-shadow-lg">
                  {spec.name}
                </h3>
                {spec.featured && (
                  <span className="absolute top-3 right-3 bg-aiorange text-black text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-md shadow-lg">
                    Most Popular
                  </span>
                )}
              </div>

              {/* Body */}
              <div className="flex flex-1 flex-col p-6">
                <p className="font-semibold text-gray-800 leading-snug">
                  {spec.desc}
                </p>
                <p className="mt-3 flex-1 text-sm text-gray-500 leading-relaxed">
                  {spec.about}
                </p>
                <Link
                  to="/RFC/guidance" target="_blank" rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-aiblue hover:text-aiorange transition-colors"
                >
                  Learn more
                  <ArrowRight size={16} strokeWidth={2.5} />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
